// Signatures (SIGN-FE-01, CdC SIG-01): the requests that concern the caller, in two boxes, "À signer"
// (the caller is a signer) and "Envoyées" (the caller asked for them), with a status filter. Each row
// shows the title, the document, who asked, how many have signed, the status and the expiry. A request
// opens on its own page (the notifications and the e-mails link there).
import { EmptyState, FormField, SkeletonList, isolate } from '@batiflow/ui';
import { Select, Tab, TabList, __css, tokens } from '@fluentui/react-components';
import { DocumentSignature24Regular } from '@fluentui/react-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppLink } from '@/shared/components/app-link';
import { useSignatureList } from './api';
import { ErrorBar, RequestStatusChip, useWhen } from './bits';
import {
  BOXES,
  STATUSES,
  isBox,
  isStatus,
  progressOf,
  type Box,
  type SignatureRequest,
  type SignatureRequestStatus,
} from './model';
import { SignaturesFrame } from './signatures-frame';

const useStyles = __css({"tools":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"fgs5rwf","Brf1p80":"f1869bpl","i8kkvl":"f1xcgk68","Belr9w4":"f1wn3y0l"},"filter":{"Bgto7am":"fd0p36q"},"list":{"dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"fgdu4nw","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l"},"row":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","Brf1p80":"f1869bpl","i8kkvl":"f1xcgk68","Belr9w4":"f1wn3y0l","Dbcxam":0,"rjzwhg":0,"Bblux5w":"fnllxr2","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"fjshy37","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"f1o0qvyv","De3pzq":"f1dmdbja"},"main":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1j0q4x9","Bgto7am":"fuzgk9b","Bh6795r":"fqerorx","xawz":"fuk1rnt"},"title":{"Bhrd7zp":"fl43uef","Be2twd7":"fod5ikn","Bg96gwp":"faaz57k","Btd35i7":"fjksvth"},"meta":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"fkfq4zb","Be2twd7":"fkhj508","Bg96gwp":"f1i3iumi","Btd35i7":"fjksvth"},"side":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"f1xcgk68","Belr9w4":"f778t4r"},"progress":{"B7salou":"f1pbl6dw","Huce71":"fz5stix"},"yours":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fkhj508","Bhrd7zp":"fl43uef","sj55zd":"f16muhyy"}});

function Row({ orgId, request }: { orgId: string; request: SignatureRequest }) {
  const { t } = useTranslation('signatures');
  const styles = useStyles();
  const { day } = useWhen();
  const { signed, total } = progressOf(request);
  // When it expires, ended or was made, according to the status.
  const dates: Record<SignatureRequestStatus, string> = {
    pending: t('list.expiresOn', { date: day(request.expiresAt) }),
    expired: t('list.expiredOn', { date: day(request.expiresAt) }),
    completed: t('list.completedOn', { date: day(request.completedAt ?? request.createdAt) }),
    declined: t('list.createdOn', { date: day(request.createdAt) }),
    cancelled: t('list.createdOn', { date: day(request.createdAt) }),
  };
  return (
    <li className={styles.row}>
      <div className={styles.main}>
        <AppLink
          to="/o/$orgId/signatures/$requestId"
          params={{ orgId, requestId: request.id }}
          className={styles.title}
        >
          <bdi>{request.title}</bdi>
        </AppLink>
        <p className={styles.meta}>
          {t('list.document', { name: isolate(request.document.name) })}
        </p>
        <p className={styles.meta}>
          {t('list.by', { name: isolate(request.requestedBy.name) })}
          {' · '}
          {dates[request.status]}
        </p>
        {request.me.canSign && request.status === 'pending' ? (
          <p className={styles.yours}>{t('list.yourTurn')}</p>
        ) : null}
      </div>
      <div className={styles.side}>
        <span className={styles.progress}>{t('list.progress', { signed, total })}</span>
        <RequestStatusChip status={request.status} />
      </div>
    </li>
  );
}

function RequestList({
  orgId,
  box,
  status,
  onClear,
}: {
  orgId: string;
  box: Box;
  status: SignatureRequestStatus | '';
  onClear: () => void;
}) {
  const { t } = useTranslation('signatures');
  const styles = useStyles();
  const list = useSignatureList(orgId, box, status);
  if (list.isError)
    return (
      <ErrorBar
        title={t('list.loadFailedTitle')}
        body={t('list.loadFailedBody')}
        onRetry={() => {
          void list.refetch();
        }}
      />
    );
  if (list.isPending) return <SkeletonList count={4} />;
  const items = list.data.items;
  if (items.length === 0)
    return status ? (
      <EmptyState
        illustration={<DocumentSignature24Regular />}
        title={t('list.empty.filteredTitle')}
        description={t('list.empty.filteredBody', { status: t(`statuses.${status}`) })}
        primaryAction={{ label: t('list.empty.clear'), onClick: onClear }}
      />
    ) : (
      <EmptyState
        illustration={<DocumentSignature24Regular />}
        title={t(`list.empty.${box}.title`)}
        description={t(`list.empty.${box}.body`)}
      />
    );
  return (
    <ul className={styles.list} aria-label={t(`list.label.${box}`)}>
      {items.map((request) => (
        <Row key={request.id} orgId={orgId} request={request} />
      ))}
    </ul>
  );
}

export function SignaturesPage({ orgId }: { orgId: string }) {
  const { t } = useTranslation('signatures');
  const styles = useStyles();
  const [box, setBox] = useState<Box>('to_sign');
  // What waits for the caller is what the first box is for; the second shows everything.
  const [status, setStatus] = useState<SignatureRequestStatus | ''>('pending');

  return (
    <SignaturesFrame>
      <div className={styles.tools}>
        <TabList
          selectedValue={box}
          aria-label={t('tabs.label')}
          onTabSelect={(_event, data) => {
            if (!isBox(data.value) || data.value === box) return;
            setBox(data.value);
            setStatus(data.value === 'to_sign' ? 'pending' : '');
          }}
        >
          {BOXES.map((id) => (
            <Tab key={id} id={`signatures-tab-${id}`} value={id}>
              {t(`tabs.${id}`)}
            </Tab>
          ))}
        </TabList>
        <FormField label={t('filter.label')} className={styles.filter}>
          <Select
            value={status}
            onChange={(_event, data) => {
              setStatus(isStatus(data.value) ? data.value : '');
            }}
          >
            <option value="">{t('filter.all')}</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {t(`statuses.${value}`)}
              </option>
            ))}
          </Select>
        </FormField>
      </div>
      <div role="tabpanel" id={`signatures-panel-${box}`} aria-labelledby={`signatures-tab-${box}`}>
        <RequestList
          orgId={orgId}
          box={box}
          status={status}
          onClear={() => {
            setStatus('');
          }}
        />
      </div>
    </SignaturesFrame>
  );
}


import "/opt/batiflow/app/apps/web/src/features/signatures/signatures-page.griffel.css";